/**
 * AuditTimelineItem - Item de timeline de movimentações (auditoria).
 *
 * Compartilhado entre o modal de detalhes de instância e o modal de detalhes
 * de inbox, garantindo a mesma linguagem visual (bolinha + ícone, transição
 * "de → para", autor e data).
 *
 * RELEASE:
 *   2026-08-08 v1.0.0 - Extração do subcomponente `TimelineItem`
 *                       (InstanceDetailsModal), com labels em português e
 *                       suporte a eventos de inbox (vínculo/desvínculo).
 */
import {
  Clock,
  History,
  RefreshCw,
  Link2,
  Unlink,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Server,
  Inbox as InboxIcon,
  KeyRound,
  Pencil,
  Trash2,
  PowerOff,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/** Evento genérico de auditoria (instância ou inbox) */
export interface AuditTimelineEvent {
  id: string;
  action_type: string;
  old_status: string | null;
  new_status: string | null;
  performed_by_type: string | null;
  performed_by: string | null;
  created_at: string;
}

/** Ícone por tipo de ação (cobre instâncias e inboxes) */
const ICON_MAP: Record<string, typeof Clock> = {
  status_change: RefreshCw,
  status_changed: RefreshCw,
  link: Link2,
  linked: Link2,
  instance_linked: Link2,
  unlink: Unlink,
  unlinked: Unlink,
  instance_unlinked: Unlink,
  suspend: AlertTriangle,
  reactivate: CheckCircle2,
  cancel: XCircle,
  create: Server,
  created: InboxIcon,
  activate: CheckCircle2,
  activated: CheckCircle2,
  deactivated: PowerOff,
  released: CheckCircle2,
  updated: Pencil,
  credentials_updated: KeyRound,
  deleted: Trash2,
};

/** Rótulo amigável em português por tipo de ação */
const LABEL_MAP: Record<string, string> = {
  status_change: 'Mudança de status',
  status_changed: 'Mudança de status',
  created: 'Criada',
  create: 'Criada',
  updated: 'Atualizada',
  activated: 'Ativada',
  activate: 'Ativada',
  deactivated: 'Desativada',
  deleted: 'Excluída',
  released: 'Liberada',
  link: 'Vinculada',
  linked: 'Vinculada',
  unlink: 'Desvinculada',
  unlinked: 'Desvinculada',
  instance_linked: 'Instância vinculada',
  instance_unlinked: 'Instância desvinculada',
  credentials_updated: 'Credenciais atualizadas',
  suspend: 'Suspensa',
  reactivate: 'Reativada',
  cancel: 'Cancelada',
};

/** Formata data ISO no padrão BR com hora */
function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return '—';
  }
}

export function AuditTimelineItem({ event }: { event: AuditTimelineEvent }) {
  const Icon = ICON_MAP[event.action_type] || History;
  const label = LABEL_MAP[event.action_type] || event.action_type.replace(/_/g, ' ');

  return (
    <div className="flex gap-3">
      {/* Trilho vertical + bolinha com ícone */}
      <div className="flex flex-col items-center">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 border border-primary/20 shrink-0">
          <Icon className="h-3.5 w-3.5 text-primary" />
        </div>
        <div className="w-px flex-1 bg-border mt-1" />
      </div>

      <div className="flex-1 pb-4 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium first-letter:uppercase">{label}</span>
          {event.old_status && event.new_status && (
            <Badge variant="outline" className="text-[10px] font-mono">
              {event.old_status} → {event.new_status}
            </Badge>
          )}
          {event.performed_by_type && (
            <Badge variant="outline" className="text-[10px] bg-muted/50">
              {event.performed_by_type}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatDate(event.created_at)}
          {event.performed_by && (
            <span className="ml-2 font-mono">por {event.performed_by}</span>
          )}
        </p>
      </div>
    </div>
  );
}
